import BankSetting from '@/api/banksetting'

interface State {
  bank: string
  bankCode: string
  siten: string
  sitenCode: string
  accountNumber: string
  accountType: string
  sei: string
  mei: string
  bigBank: BankData
  bankNames: BankData
  sitenNames: BankData
  error: string
}

interface BankData {
  [name: string]: string
}

export interface FetchBankNamesParams {
  first: string
}

export interface FetchSitenNamesParams {
  siten_first: string
}

const defaultState: State = {
  bank: '',
  bankCode: '',
  siten: '',
  sitenCode: '',
  accountNumber: '',
  accountType: '1',
  sei: '',
  mei: '',
  bigBank: {},
  bankNames: {},
  sitenNames: {},
  error: ''
}

const state: State = { ...defaultState }

const mutations = {
  resetState(state: State) {
    const s = defaultState
    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  },

  setBank(state: State, bank: string) {
    state.bank = bank
  },

  setBankCode(state: State, bankCode: string) {
    state.bankCode = bankCode
  },

  setSiten(state: State, siten: string) {
    state.siten = siten
  },

  setSitenCode(state: State, sitenCode: string) {
    state.sitenCode = sitenCode
  },

  setAccountNumber(state: State, accountNumber: string) {
    state.accountNumber = accountNumber
  },

  setAccountType(state: State, accountType: string) {
    state.accountType = accountType
  },

  setSei(state: State, sei: string) {
    state.sei = sei
  },

  setMei(state: State, mei: string) {
    state.mei = mei
  },

  setBigBank(state: State, bigBank: BankData) {
    state.bigBank = bigBank
  },

  setBankNames(state: State, bankNames: BankData) {
    state.bankNames = bankNames
  },

  setSitenNames(state: State, sitenNames: BankData) {
    state.sitenNames = sitenNames
  },

  setError(state: State, error: string) {
    state.error = error
  }
}

const actions = {
  fetchBankSetting({ commit }, params): Promise<any> {
    return BankSetting.fetchBankSetting(params)
      .then(data => {
        commit('setBank', data.bank)
        commit('setBankCode', data.bank_code)
        commit('setSiten', data.siten)
        commit('setSitenCode', data.siten_code)
        commit('setAccountNumber', data.kouza_no)
        commit('setAccountType', data.type)
        commit('setSei', data.sei)
        commit('setMei', data.mei)
        return data
      })
      .catch(err => {
        throw err
      })
  },

  /**
   * 銀行名一覧を取得
   */
  fetchBankNames({ commit }, params: FetchBankNamesParams): Promise<any> {
    return BankSetting.fetchBankNames({
      ...params
    })
      .then(data => {
        if (data.error) {
          console.error(data.error)
          return data
        }
        commit('setBigBank', data.big_bank)
        commit('setBankNames', data[params.first])
        return data
      })
      .catch(err => {
        throw err
      })
  },

  /**
   * 支店名一覧を取得
   */
  fetchSitenNames({ state, commit }, params: FetchSitenNamesParams): Promise<any> {
    return BankSetting.fetchSitenNames({
      bank_code: state.bankCode,
      ...params
    })
      .then(data => {
        commit('setSitenNames', data[state.bankCode])
        return data
      })
      .catch(err => {
        throw err
      })
  },

  /**
   * 口座情報ををサーバに送信
   */
  submit({ state }, params): Promise<any> {
    return BankSetting.submit({
      ...params,
      bank: state.bank,
      bank_code: state.bankCode,
      siten: state.siten,
      siten_code: state.sitenCode,
      kouza_no: state.accountNumber,
      type: state.accountType,
      sei: state.sei,
      mei: state.mei
    }).catch(err => {
      throw err
    })
  }
}

const getters = {
  bank: (state: State): string => state.bank,
  bankCode: (state: State): string => state.bankCode,
  siten: (state: State): string => state.siten,
  siteCode: (state: State): string => state.sitenCode,
  accountNumber: (state: State): string => state.accountNumber,
  accountType: (state: State): string => state.accountType,
  sei: (state: State): string => state.sei,
  mei: (state: State): string => state.mei,
  bigBank: (state: State): BankData => state.bigBank,
  bankNames: (state: State): BankData => state.bankNames,
  sitenNames: (state: State): BankData => state.sitenNames,
  error: (state: State): string => state.error
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
